<template>
  <div @click="handleDelegatedClick">
    <!-- States -->
    <div class="section">
      <h4>{{ t('Estados') }}</h4>
      <data-table :config="statesTableConfig" :data="states" :auto-load="false">
      </data-table>
    </div>

    <!-- Cities -->
    <div class="section">
      <h4>{{ t('Cidades') }}</h4>
      <data-table :config="citiesTableConfig" :data="cities" :auto-load="false">
      </data-table>
    </div>

    <!-- Neighborhoods -->
    <div class="section">
      <h4>{{ t('Bairros') }}</h4>
      <data-table :config="neighborhoodsTableConfig" :data="neighborhoods" :auto-load="false">
      </data-table>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

type Id = number | string;

interface LocationItem {
  ID?: Id;
  Name?: string;
  [key: string]: unknown;
}

interface LocationsData {
  States?: LocationItem[];
  Cities?: LocationItem[];
  Neighborhoods?: LocationItem[];
  [key: string]: unknown;
}

interface CellParams {
  data: Record<string, unknown>;
  value?: unknown;
}

interface Props {
  franquiaId: number;
  locationsData?: LocationsData;
}

const props = withDefaults(defineProps<Props>(), {
  locationsData: () => ({
    States: [],
    Cities: [],
    Neighborhoods: [],
  }),
});

const emit = defineEmits<{
  'remove-location': [locationType: string, locationId: Id];
}>();

const { t } = useI18n();

// Computed
const states = computed<LocationItem[]>(() => props.locationsData?.States || []);
const cities = computed<LocationItem[]>(() => props.locationsData?.Cities || []);
const neighborhoods = computed<LocationItem[]>(() => props.locationsData?.Neighborhoods || []);

// Methods
const handleRemove = (locationType: string, locationId: Id): void => {
  emit('remove-location', locationType, locationId);
};

// Event delegation handler for remove buttons
const handleDelegatedClick = (event: MouseEvent): void => {
  const target = event.target as HTMLElement | null;
  if (!target) return;

  // Check if clicked element or its parent has the remove button class
  const removeBtn = target.closest('.remove-location-btn') as HTMLElement | null;
  if (removeBtn) {
    const locationType = removeBtn.dataset.type as string;
    const locationId = parseInt(removeBtn.dataset.id as string, 10);
    handleRemove(locationType, locationId);
  }
};

// States Table Configuration
const statesTableConfig = computed(() => ({
  tableId: 'tabelaEstados',
  columns: [
    {
      data: 'Name',
      title: t('Nome'),
      sortable: false,
      width: 250
    },
    {
      data: 'CountryName',
      title: t('País'),
      sortable: false,
      width: 150
    },
    {
      data: 'actions',
      title: t('Ações'),
      width: 120,
      sortable: false,
      cellRenderer: (params: CellParams): Node => {
        const container = document.createElement('div');
        container.className = 'action-buttons';
        container.style.display = 'flex';
        container.style.gap = '4px';
        container.style.justifyContent = 'center';

        const row = (params.data || {}) as { ID?: Id };
        const stateId = row.ID;

        // Use a button with data attributes for event delegation
        container.innerHTML = `<button type="button" class="p-button p-button-sm p-button-danger remove-location-btn" data-testid="franquias-locations-state-remove-${stateId}" data-type="state" data-id="${stateId}" title="${t('Remover')}"><i class="pi pi-ban"></i></button>`;

        return container;
      },
    }
  ],
  pageLength: 100,
  pagination: false,
  heightMode: 'auto',
  footer: false,
  resizableColumns: true,
  stripedRows: true,
  showGridlines: true,
  // Disable filters
  showFilters: false,
  // Disable sorting
  sortable: false,
}));

// Cities Table Configuration
const citiesTableConfig = computed(() => ({
  tableId: 'tabelaCidades',
  columns: [
    {
      data: 'Name',
      title: t('Nome'),
      sortable: false,
      width: 250
    },
    {
      data: 'StateName',
      title: t('Estado'),
      sortable: false,
      width: 150
    },
    {
      data: 'actions',
      title: t('Ações'),
      width: 120,
      sortable: false,
      cellRenderer: (params: CellParams): Node => {
        const container = document.createElement('div');
        container.className = 'action-buttons';
        container.style.display = 'flex';
        container.style.gap = '4px';
        container.style.justifyContent = 'center';

        // Remove button with data attributes for event delegation
        const row = (params.data || {}) as { ID?: Id };
        const cityId = row.ID;

        // Use a button with data attributes instead of event listeners
        container.innerHTML = `<button type="button" class="p-button p-button-sm p-button-danger remove-location-btn" data-testid="franquias-locations-city-remove-${cityId}" data-type="city" data-id="${cityId}" title="${t('Remover')}"><i class="pi pi-ban"></i></button>`;

        return container;
      },
    }
  ],
  pageLength: 100,
  pagination: false,
  heightMode: 'auto',
  footer: false,
  resizableColumns: true,
  stripedRows: true,
  showGridlines: true,
  // Disable filters
  showFilters: false,
  // Disable sorting
  sortable: false,
}));

// Neighborhoods Table Configuration
const neighborhoodsTableConfig = computed(() => ({
  tableId: 'tabelaBairros',
  columns: [
    {
      data: 'Name',
      title: t('Nome'),
      sortable: false,
      width: 250
    },
    {
      data: 'CityName',
      title: t('Cidade'),
      sortable: false,
      width: 150
    },
    {
      data: 'actions',
      title: t('Ações'),
      width: 120,
      sortable: false,
      cellRenderer: (params: CellParams): Node => {
        const container = document.createElement('div');
        container.className = 'action-buttons';
        container.style.display = 'flex';
        container.style.gap = '4px';
        container.style.justifyContent = 'center';

        const row = (params.data || {}) as { ID?: Id };
        const neighborhoodId = row.ID;

        // Use a button with data attributes for event delegation
        container.innerHTML = `<button type="button" class="p-button p-button-sm p-button-danger remove-location-btn" data-testid="franquias-locations-neighborhood-remove-${neighborhoodId}" data-type="neighborhood" data-id="${neighborhoodId}" title="${t('Remover')}"><i class="pi pi-ban"></i></button>`;

        return container;
      },
    }
  ],
  pageLength: 100,
  pagination: false,
  heightMode: 'auto',
  footer: false,
  resizableColumns: true,
  stripedRows: true,
  showGridlines: true,
  size: 'small',
  // Disable filters
  showFilters: false,
  // Disable sorting
  sortable: false,
}));
</script>

<style scoped>
.section {
  margin-bottom: 30px;
}

.section h4 {
  margin-top: 0;
  margin-bottom: 15px;
  font-size: 1.2rem;
  font-weight: 600;
  color: #333;
}

/* Action buttons styling */
:deep(.action-buttons) {
  display: flex;
  gap: 4px;
  justify-content: center;
  align-items: center;
}

:deep(.action-buttons button) {
  margin: 0 !important;
}
</style>
